/**
 * Build a Supabase Storage image-transform URL from a public object URL.
 * Cuts egress by serving correctly-sized variants for each use case.
 *
 * Input URL example:
 *   https://<ref>.supabase.co/storage/v1/object/public/event-covers/abc.webp
 * Output URL:
 *   https://<ref>.supabase.co/storage/v1/render/image/public/event-covers/abc.webp?width=480&quality=75&resize=cover
 */

export type CoverPreset = 'thumb' | 'card' | 'hero' | 'logo' | 'favicon';

const PRESETS: Record<CoverPreset, { width: number; quality: number; resize?: 'cover' | 'contain' | 'fill' }> = {
  thumb:   { width: 160,  quality: 70, resize: 'cover' },
  card:    { width: 480,  quality: 75, resize: 'cover' },
  hero:    { width: 1600, quality: 78, resize: 'cover' },
  logo:    { width: 512,  quality: 85, resize: 'contain' },
  favicon: { width: 128,  quality: 85, resize: 'contain' },
};

export interface TransformOpts {
  width?: number;
  height?: number;
  quality?: number;
  resize?: 'cover' | 'contain' | 'fill';
}

function isSupabaseObjectUrl(url: string): boolean {
  return /\/storage\/v1\/object\/public\//.test(url);
}

// Toggle Supabase Storage image transformations. Requires the Pro plan with
// "Image Transformations" enabled in the project. When disabled (or on Free),
// the /render/image/public endpoint returns 400 and breaks every <img>.
// Keep this false until transformations are confirmed enabled in the dashboard.
const TRANSFORMS_ENABLED = false;

export function coverUrl(url: string | null | undefined, presetOrOpts: CoverPreset | TransformOpts = 'card'): string {
  if (!url) return '';
  if (!isSupabaseObjectUrl(url)) return url; // external URL, leave alone
  if (!TRANSFORMS_ENABLED) return url;       // serve original (already WebP+resized at upload)

  const opts: TransformOpts =
    typeof presetOrOpts === 'string' ? PRESETS[presetOrOpts] : presetOrOpts;

  const params = new URLSearchParams();
  if (opts.width) params.set('width', String(opts.width));
  if (opts.height) params.set('height', String(opts.height));
  if (opts.quality) params.set('quality', String(opts.quality));
  if (opts.resize) params.set('resize', opts.resize);

  return url.replace('/storage/v1/object/public/', '/storage/v1/render/image/public/') + `?${params.toString()}`;
}

/** Generate a srcset for responsive images. Use with `<img sizes="...">`. */
export function coverSrcSet(url: string | null | undefined, widths: number[], quality = 75): string {
  if (!url || !isSupabaseObjectUrl(url)) return '';
  return widths
    .map((w) => `${coverUrl(url, { width: w, quality, resize: 'cover' })} ${w}w`)
    .join(', ');
}
